import { useEffect, useRef } from 'react';

interface StarfieldOverlayProps {
  opacity?: number;
}

export default function StarfieldOverlay({ opacity = 0.8 }: StarfieldOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    const stars: Star[] = [];
    let width = window.innerWidth;
    let height = window.innerHeight;
    const centerX = width / 2;
    const centerY = height / 2;

    const setCanvasSize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };
    setCanvasSize();

    // Parse CSS color string (hex or rgb/rgba) to [r,g,b]
    const parseCssColorToRgb = (input: string): [number, number, number] => {
      let c = input.trim();
      if (!c) return [10, 116, 255];
      const rgbMatch = c.match(/^rgba?\(([^)]+)\)$/i);
      if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map(v => parseFloat(v.trim()));
        return [
          Math.max(0, Math.min(255, parts[0] || 0)),
          Math.max(0, Math.min(255, parts[1] || 0)),
          Math.max(0, Math.min(255, parts[2] || 0)),
        ];
      }
      if (c.startsWith('#')) c = c.slice(1);
      if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
      if (c.length !== 6) return [10, 116, 255];
      const r = parseInt(c.slice(0,2), 16);
      const g = parseInt(c.slice(2,4), 16);
      const b = parseInt(c.slice(4,6), 16);
      return [r,g,b];
    };

    // Accent rgba base string updated each frame
    let colorBase = 'rgba(10, 116, 255,';
    // Background rgba base string updated each frame (theme-aware)
    let bgFill = 'rgba(0, 0, 0, 0.1)';

    class Star {
      x: number;
      y: number;
      z: number;
      color: string;
      pulse: number;

      constructor() {
        this.x = 0;
        this.y = 0;
        this.z = 0;
        this.color = '';
        this.pulse = 0;
        this.reset();
        this.z = Math.random() * width;
      }

      reset() {
        this.x = (Math.random() - 0.5) * width;
        this.y = (Math.random() - 0.5) * height;
        this.z = width;
        this.color = `${colorBase} 0.8)`;
        this.pulse = Math.random() * Math.PI * 2;
      }

      update() {
        this.z -= 5;
        this.pulse += 0.1;

        if (this.z <= 0) {
          this.reset();
        }
      }

      draw() {
        const sx = (this.x / this.z) * width + centerX;
        const sy = (this.y / this.z) * height + centerY;
        const size = (1 - this.z / width) * 3;
        const pulseFactor = Math.sin(this.pulse) * 0.3 + 0.7;

        if (sx >= 0 && sx <= width && sy >= 0 && sy <= height) {
          ctx.beginPath();
          ctx.arc(sx, sy, size * pulseFactor, 0, Math.PI * 2);
          ctx.fillStyle = this.color;
          ctx.fill();

          const prevZ = this.z + 5;
          const prevSx = (this.x / prevZ) * width + centerX;
          const prevSy = (this.y / prevZ) * height + centerY;

          const gradient = ctx.createLinearGradient(prevSx, prevSy, sx, sy);
          gradient.addColorStop(0, `${colorBase} 0)`);
          gradient.addColorStop(1, this.color);

          ctx.beginPath();
          ctx.moveTo(prevSx, prevSy);
          ctx.lineTo(sx, sy);
          ctx.strokeStyle = gradient;
          ctx.lineWidth = size;
          ctx.stroke();
        }
      }
    }

    for (let i = 0; i < 200; i++) {
      stars.push(new Star());
    }

    const animate = () => {
      // Read theme and colors each frame to adapt to changes
      const styles = getComputedStyle(document.documentElement);
      const isLight = document.documentElement.getAttribute('data-theme') === 'light';
      const bgVar = styles.getPropertyValue('--color-background').trim();
      const [br, bgc, bb] = parseCssColorToRgb(bgVar || (isLight ? '#ffffff' : '#000000'));
      const bgAlpha = isLight ? 0.06 : 0.1; // softer trail in light mode
      bgFill = `rgba(${br}, ${bgc}, ${bb}, ${bgAlpha})`;
      ctx.fillStyle = bgFill;
      ctx.fillRect(0, 0, width, height);

      // Read header accent each frame to respond to theme/accent changes
      const accent = styles.getPropertyValue('--color-header-accent').trim() || '#0A74FF';
      const [r,g,b] = parseCssColorToRgb(accent);
      colorBase = `rgba(${r}, ${g}, ${b},`;

      stars.forEach(star => {
        star.update();
        star.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      setCanvasSize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [opacity]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -1,
        opacity: opacity
      }}
    />
  );
}
